const DEFAULT_DISPLAY_FRAME_RATE = 60;
const MIN_DISPLAY_FRAME_INTERVAL_MS = 4;
const MAX_DISPLAY_FRAME_INTERVAL_MS = 50;
const MIN_FRAME_INTERVAL_SAMPLES = 10;
const MAX_FRAME_INTERVAL_SAMPLES = 60;
const FRAME_PROCESSING_BUDGET_FRACTION = 2 / 3;
const MIN_FRAME_PROCESSING_BUDGET_MS = 1;
const MAX_FRAME_PROCESSING_BUDGET_MS = 4;

export interface FrameRateBudget {
  readonly displayFrameRate: number;
  readonly targetFrameRate: number;
  readonly targetFrameIntervalMs: number;
  readonly processingBudgetMs: number;
}

export function estimateDisplayFrameRate(frameIntervalsMs: readonly number[]): number {
  const plausibleIntervals = frameIntervalsMs
    .filter((intervalMs) => intervalMs >= MIN_DISPLAY_FRAME_INTERVAL_MS && intervalMs <= MAX_DISPLAY_FRAME_INTERVAL_MS)
    .sort((a, b) => a - b);
  if (plausibleIntervals.length < MIN_FRAME_INTERVAL_SAMPLES) {
    return DEFAULT_DISPLAY_FRAME_RATE;
  }

  // Fast intervals reveal the native cadence even when occasional frames are missed.
  const intervalMs = plausibleIntervals[Math.floor((plausibleIntervals.length - 1) * 0.2)];
  return 1000 / intervalMs;
}

export function calculateFrameProcessingBudgetMs(targetFrameRate: number): number {
  if (!Number.isFinite(targetFrameRate) || targetFrameRate <= 0) {
    throw new Error("Target frame rate must be a positive finite number");
  }
  const frameIntervalMs = 1000 / targetFrameRate;
  return Math.min(
    MAX_FRAME_PROCESSING_BUDGET_MS,
    Math.max(MIN_FRAME_PROCESSING_BUDGET_MS, frameIntervalMs * FRAME_PROCESSING_BUDGET_FRACTION),
  );
}

/** Tracks display cadence and derives the budget for cooperative per-frame work. */
export class TargetFrameRate {
  private readonly frameIntervalsMs: number[] = [];
  private previousFrameTimeMs: number | undefined;
  private configuredTargetFrameRate: number | undefined;

  recordAnimationFrame(frameTimeMs: number): void {
    if (this.previousFrameTimeMs !== undefined) {
      const intervalMs = frameTimeMs - this.previousFrameTimeMs;
      if (intervalMs > 0) {
        this.frameIntervalsMs.push(intervalMs);
        if (this.frameIntervalsMs.length > MAX_FRAME_INTERVAL_SAMPLES) {
          this.frameIntervalsMs.shift();
        }
      }
    }
    this.previousFrameTimeMs = frameTimeMs;
  }

  setTargetFrameRate(targetFrameRate: number | undefined): void {
    if (targetFrameRate !== undefined && (!Number.isFinite(targetFrameRate) || targetFrameRate <= 0)) {
      throw new Error("Target frame rate must be a positive finite number");
    }
    this.configuredTargetFrameRate = targetFrameRate;
  }

  getBudget(): FrameRateBudget {
    const displayFrameRate = estimateDisplayFrameRate(this.frameIntervalsMs);
    const targetFrameRate = Math.min(this.configuredTargetFrameRate ?? displayFrameRate, displayFrameRate);
    return {
      displayFrameRate,
      targetFrameRate,
      targetFrameIntervalMs: 1000 / targetFrameRate,
      processingBudgetMs: calculateFrameProcessingBudgetMs(targetFrameRate),
    };
  }
}
